import domtoimage from 'dom-to-image';
import jsPDF from 'jspdf';

// Estratégia de exportação da DRE:
// - Combinação `dom-to-image + jsPDF` (mesma do resto do projeto, ex:
//   src/screens/OrderServiceBudget/ModalBudget/ModalBudget.jsx). Captura
//   bem o DOM com SVG/CSS e funciona consistentemente, ao contrário do
//   html2pdf.js que estava gerando PDF em branco.
// - Wrapper VISÍVEL na viewport durante o snapshot (overlay esconde do
//   usuário). Tema claro forçado via <style> + atributos data-fd-keep-color
//   pra preservar cores semânticas (verde lucro, vermelho prejuízo).

const PRINT_STYLE_ID = 'fd-pdf-print-style';

const PRINT_CSS = `
  .fd-pdf-print {
    background: #ffffff !important;
    color: #111111 !important;
    font-family: Inter, Arial, sans-serif;
  }
  .fd-pdf-print * {
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    border-color: #cccccc !important;
    text-shadow: none !important;
  }
  .fd-pdf-print table,
  .fd-pdf-print thead,
  .fd-pdf-print tbody,
  .fd-pdf-print tr {
    background: #ffffff !important;
  }
  .fd-pdf-print thead th {
    background: #f3f3f3 !important;
    color: #111 !important;
    border-bottom: 1px solid #cccccc !important;
  }
  .fd-pdf-print td {
    border-bottom: 1px solid #eeeeee !important;
  }
  .fd-pdf-print *:not([data-fd-keep-color]) {
    color: #111 !important;
  }
`;

const ensurePrintStyle = () => {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const styleEl = document.createElement('style');
  styleEl.id = PRINT_STYLE_ID;
  styleEl.textContent = PRINT_CSS;
  document.head.appendChild(styleEl);
};

const markSemanticColors = (rootEl) => {
  const all = [rootEl, ...rootEl.querySelectorAll('*')];
  all.forEach((el) => {
    const inlineColor = (el.getAttribute('style') || '')
      .match(/(?:^|;)\s*color\s*:\s*([^;]+)/i)?.[1]
      ?.trim();
    if (!inlineColor) return;
    if (/var\(--/i.test(inlineColor)) return;
    const cleaned = inlineColor.replace('#', '').toLowerCase();
    if (/^(fff|ffffff|white|f0f0f0|e0e0e0|ccc|cccccc|999|999999)$/.test(cleaned)) return;
    el.setAttribute('data-fd-keep-color', '');
  });
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const buildOverlay = () => {
  const overlay = document.createElement('div');
  overlay.id = 'fd-pdf-loading-overlay';
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.92);
    z-index: 100000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
    pointer-events: all;
  `;
  overlay.innerHTML = `
    <div style="
      width: 48px; height: 48px;
      border: 4px solid rgba(255,255,255,0.2);
      border-top-color: #5BC424;
      border-radius: 50%;
      animation: fd-pdf-spin 0.9s linear infinite;
      margin-bottom: 18px;
    "></div>
    <div style="font-size: 16px; font-weight: 600; letter-spacing: 0.5px;">Gerando PDF...</div>
    <div style="font-size: 12px; color: rgba(255,255,255,0.6); margin-top: 6px;">Aguarde alguns segundos</div>
    <style>
      @keyframes fd-pdf-spin { to { transform: rotate(360deg); } }
    </style>
  `;
  return overlay;
};

export const exportDREToPDF = async ({ elementId, periodLabel, companyName }) => {
  const source = document.getElementById(elementId);
  if (!source) throw new Error(`Element ${elementId} not found`);

  ensurePrintStyle();

  const wrapper = document.createElement('div');
  wrapper.className = 'fd-pdf-print';
  wrapper.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 1100px;
    padding: 20px;
    background: #ffffff;
    z-index: 99999;
    overflow: visible;
  `;

  const header = document.createElement('div');
  header.style.cssText =
    'padding:0 0 12px 0; border-bottom:2px solid #111; margin-bottom:16px; background:#fff;';
  const title = document.createElement('div');
  title.style.cssText = 'font-size:22px;font-weight:700;letter-spacing:1px;color:#111;';
  title.setAttribute('data-fd-keep-color', '');
  title.textContent = `DRE — ${companyName}`;

  const subtitle = document.createElement('div');
  subtitle.style.cssText = 'font-size:13px;color:#555;margin-top:6px;';
  subtitle.setAttribute('data-fd-keep-color', '');
  subtitle.textContent = `Período: ${periodLabel} · Gerado em ${new Date().toLocaleDateString('pt-BR')}`;

  header.appendChild(title);
  header.appendChild(subtitle);

  const clone = source.cloneNode(true);
  markSemanticColors(clone);

  wrapper.appendChild(header);
  wrapper.appendChild(clone);

  const overlay = buildOverlay();
  document.body.appendChild(wrapper);
  document.body.appendChild(overlay);

  // Aguarda 2 frames + tick extra para o navegador calcular layout
  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );
  await wait(200);

  const filename = `DRE_${periodLabel.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`;

  try {
    const width = wrapper.offsetWidth;
    const height = wrapper.offsetHeight;

    if (width === 0 || height === 0) {
      throw new Error('Wrapper com dimensões inválidas');
    }

    // Captura o wrapper como PNG via dom-to-image (mesma lib usada em
    // OrderServiceBudget/ModalBudget pra geração de PDF do orçamento/OS)
    const dataUrl = await domtoimage.toPng(wrapper, {
      quality: 0.95,
      cacheBust: true,
      bgcolor: '#ffffff',
      width,
      height,
    });

    // Cria PDF com tamanho exato do conteúdo (formato customizado)
    const pdf = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [width, height],
    });

    pdf.addImage(dataUrl, 'PNG', 0, 0, width, height);
    pdf.save(filename);
  } finally {
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }
};
